import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'

import { useAuthStore } from '@/features/auth/store/authStore'
import { normalizeApiError } from '@/shared/api/ApiError'
import { notify } from '@/shared/lib/notify'

import { profileApi } from '../api/profileApi'
import { useProfileFeedbackStore } from '../store/profileFeedbackStore'
import type {
  NotificationPreferences,
  UpdatePreferencesPayload,
  UpdateProfilePayload,
  UserProfile,
  UserStats,
} from '../types/profile.types'

export const profileKeys = {
  current: ['profile', 'current'] as const,
  stats: ['profile', 'stats'] as const,
}

/**
 * Carga el perfil del usuario actual. {@code enabled} permite que componentes
 * públicos (p.ej. JoinCallToAction en SessionDetailPage) solo disparen la
 * llamada cuando hay sesión — si no, GET /me/profile devuelve 401 y el
 * interceptor de httpClient hace un redirect duro a /login (bug 2026-06-03).
 *
 * <p>{@code fresh}: bypasa el cache (staleTime 0 + refetchOnMount always).
 * Útil para componentes que necesitan reaccionar a cambios del trafficLight
 * / cooldowns al instante (p.ej. el botón "Unirme" debe mostrar el modal
 * RiskyJoinConfirm o CooldownBlocked aunque el usuario haya cambiado de
 * estado desde el último mount).</p>
 */
export function useProfileQuery(options?: { enabled?: boolean; fresh?: boolean }) {
  const fresh = options?.fresh ?? false
  return useQuery<UserProfile>({
    queryKey: profileKeys.current,
    queryFn: () => profileApi.get(),
    staleTime: fresh ? 0 : 30_000,
    refetchOnMount: fresh ? 'always' : true,
    enabled: options?.enabled ?? true,
  })
}

export function useProfileStatsQuery(options?: { enabled?: boolean }) {
  return useQuery<UserStats>({
    queryKey: profileKeys.stats,
    queryFn: () => profileApi.getStats(),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Mutation con optimistic update completo (patrón TanStack canónico).
 *
 * Flujo:
 * - `onMutate` cancela queries en vuelo, hace snapshot del cache y aplica el
 *   cambio inmediatamente sobre el cache. Esto es lo que el componente ve
 *   antes de que el server responda.
 * - `onError` revierte al snapshot si la mutation falla.
 * - `onSuccess` reemplaza el cache con la respuesta autoritativa del server
 *   y sincroniza el `authStore` (avatar/bio en el header).
 * - `onSettled` invalida la query para garantizar que cualquier divergencia
 *   se resuelve con un re-fetch (defensa en profundidad).
 *
 * El single source of truth es el cache `['profile', 'current']`. Los
 * componentes consumen `useProfileQuery()` directamente, sin state local.
 */
export function useUpdateProfileMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateProfilePayload) => profileApi.update(payload),

    onMutate: async (payload) => {
      // Cancelamos refetches en vuelo para que no pisen el optimistic.
      await qc.cancelQueries({ queryKey: profileKeys.current })

      const prev = qc.getQueryData<UserProfile>(profileKeys.current)
      if (!prev) return { prev }

      const next: UserProfile = { ...prev }

      if (payload.avatarCode !== undefined) {
        next.avatarCode = payload.avatarCode
      }
      if (payload.bio !== undefined) {
        next.bio = payload.bio
      }
      if (payload.provinceCode !== undefined && payload.provinceCode !== '') {
        next.provinceCode = payload.provinceCode
      }
      if (payload.cityCode !== undefined && payload.cityCode !== '') {
        next.cityCode = payload.cityCode
      }
      if (payload.areaCode !== undefined && payload.areaCode !== '') {
        next.areaCode = payload.areaCode
      }
      if (payload._favoriteGamesOptimistic !== undefined) {
        next.favoriteGames = payload._favoriteGamesOptimistic
      }

      qc.setQueryData<UserProfile>(profileKeys.current, next)
      return { prev }
    },

    onError: (err, _payload, context) => {
      if (context?.prev) {
        qc.setQueryData<UserProfile>(profileKeys.current, context.prev)
      }
      // Empuja el error al banner visible en /profile. Antes esto era un
      // console.error silencioso — durante semanas un 500 del backend hacía
      // que los favoritos "aparecieran y desaparecieran" sin que el usuario
      // viera el motivo. Ahora cualquier fallo se ve en pantalla.
      const apiError = normalizeApiError(err)
      const message =
        apiError.message ||
        i18next.t('profile.feedback.genericError', {
          defaultValue: 'Algo salió mal, prueba de nuevo.',
        })
      useProfileFeedbackStore.getState().show(message, 'error')
      console.error('useUpdateProfileMutation failed', apiError, err)
    },

    onSuccess: (profile) => {
      // El server es la verdad final — sobrescribimos el optimistic con la
      // respuesta autoritativa (incluye orden y datos de los games desde BGG).
      qc.setQueryData<UserProfile>(profileKeys.current, profile)

      // El header lee avatar/bio de `useAuthStore` (zustand, no TanStack).
      // Mergeamos los campos en el store directamente.
      const current = useAuthStore.getState().currentUser
      if (current) {
        useAuthStore.getState().setCurrentUser({
          ...current,
          selectedAvatarCode: profile.avatarCode ?? current.selectedAvatarCode,
          bio: profile.bio ?? undefined,
        })
      }

      notify.success(i18next.t('toasts.profileSaved'))
    },

    onSettled: () => {
      // Defensa en profundidad: si hubo divergencia entre optimistic y server,
      // un refetch garantiza coherencia.
      void qc.invalidateQueries({ queryKey: profileKeys.current })
    },
  })
}

/**
 * Mutation dedicada para cambiar el idioma del usuario (`users.locale`).
 *
 * NO usa optimistic update sobre el profile cache: el `localeStore` ya
 * tiene el valor del toggle aplicado y es la "verdad optimista" del FE.
 * Esta mutation se encarga solo de persistir en el backend. En
 * `onSuccess` también sincroniza `currentUser.locale` del `authStore`
 * para que un `useLocaleSync` posterior no detecte falsa divergencia.
 *
 * Usado desde el toggle del header (`LanguageToggleCompact`) y desde
 * `useLocaleSync` cuando detecta que el localStorage gana sobre backend.
 */
export function useUpdateLocaleMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (locale: 'es' | 'en') => profileApi.update({ locale }),
    onSuccess: (profile, locale) => {
      // Refresca el cache de profile con el response autoritativo.
      qc.setQueryData<UserProfile>(profileKeys.current, profile)
      // Sincroniza el authStore para que useLocaleSync no detecte
      // que backend ≠ localeStore tras esta mutation.
      const current = useAuthStore.getState().currentUser
      if (current) {
        useAuthStore.getState().setCurrentUser({ ...current, locale })
      }
    },
  })
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string
      newPassword: string
    }) => profileApi.changePassword(currentPassword, newPassword),
  })
}

export function useDeleteAccountMutation() {
  return useMutation({
    mutationFn: (password: string) => profileApi.deleteAccount(password),
  })
}

/**
 * Actualiza preferencias de notificación con optimistic update sobre el
 * objeto {@code notificationPreferences} del perfil cacheado. Mismo
 * patrón que {@link useUpdateProfileMutation}: snapshot → cambio
 * inmediato → rollback en error → sustitución por la verdad del server
 * en success → invalidación en settled como defensa en profundidad.
 */
export function useUpdatePreferencesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdatePreferencesPayload) => profileApi.updatePreferences(payload),

    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: profileKeys.current })
      const prev = qc.getQueryData<UserProfile>(profileKeys.current)
      if (!prev) return { prev }

      // Filtramos campos undefined para que el spread no pise valores
      // del cache con undefined. Cualquier campo definido (incluido
      // false) sobrescribe el cache.
      const definedFields = Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined),
      ) as Partial<NotificationPreferences>

      const next: UserProfile = {
        ...prev,
        notificationPreferences: {
          ...prev.notificationPreferences,
          ...definedFields,
        },
      }
      qc.setQueryData<UserProfile>(profileKeys.current, next)
      return { prev }
    },

    onError: (err, _payload, context) => {
      if (context?.prev) {
        qc.setQueryData<UserProfile>(profileKeys.current, context.prev)
      }
      const apiError = normalizeApiError(err)
      const message =
        apiError.message ||
        i18next.t('profile.feedback.genericError', {
          defaultValue: 'Algo salió mal, prueba de nuevo.',
        })
      useProfileFeedbackStore.getState().show(message, 'error')
    },

    onSuccess: (profile) => {
      qc.setQueryData<UserProfile>(profileKeys.current, profile)
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: profileKeys.current })
    },
  })
}
